/**
 * PermissionGate Component
 *
 * Generic component to conditionally render UI elements based on permissions
 * Supports hiding, disabling, or replacing elements when permission is denied
 */

import React from 'react';
import PropTypes from 'prop-types';
import { usePermission } from '../../hooks/usePermission';

/**
 * Permission Gate Component
 *
 * Wraps any UI element and controls its visibility/availability based on permissions
 *
 * @param {string|string[]} permission - Single permission or array of permissions
 * @param {string} mode - How to check multiple permissions: 'any' (OR) or 'all' (AND)
 * @param {ReactNode} children - The UI element to conditionally render
 * @param {ReactNode} fallback - What to show when permission is denied (default: null)
 * @param {boolean} disable - If true, disable instead of hide when permission denied
 * @param {boolean} hide - If true, hide element when permission denied (default: true)
 * @param {string} disabledTooltip - Tooltip message to show when disabled
 *
 * @example Basic usage - Hide button if no permission
 * <PermissionGate permission="ZONE_CREATE">
 *   <button>Create Zone</button>
 * </PermissionGate>
 *
 * @example Disable instead of hide
 * <PermissionGate permission="ZONE_UPDATE" disable={true}>
 *   <button>Update Zone</button>
 * </PermissionGate>
 *
 * @example Multiple permissions with OR logic
 * <PermissionGate permission={['ZONE_UPDATE', 'ZONE_DELETE']} mode="any">
 *   <button>Edit or Delete</button>
 * </PermissionGate>
 *
 * @example Multiple permissions with AND logic
 * <PermissionGate permission={['ZONE_VIEW', 'ZONE_EXPORT']} mode="all">
 *   <button>Export Zones</button>
 * </PermissionGate>
 *
 * @example Show fallback message
 * <PermissionGate
 *   permission="ZONE_CREATE"
 *   fallback={<span className="text-muted">No permission to create</span>}
 * >
 *   <button>Create Zone</button>
 * </PermissionGate>
 */
export const PermissionGate = ({
  permission,
  mode = 'any',
  children,
  fallback = null,
  disable = false,
  hide = true,
  disabledTooltip = 'You do not have permission to perform this action',
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermission();

  // Determine if user has required permission
  let hasRequiredPermission = true;

  if (permission) {
    if (Array.isArray(permission)) {
      // Multiple permissions
      if (mode === 'all') {
        hasRequiredPermission = hasAllPermissions(permission);
      } else {
        hasRequiredPermission = hasAnyPermission(permission);
      }
    } else {
      // Single permission
      hasRequiredPermission = hasPermission(permission);
    }
  }

  // If permission granted, render children as-is
  if (hasRequiredPermission) {
    return <>{children}</>;
  }

  // Permission denied - apply fallback strategy
  if (disable && !hide) {
    // Disable the element instead of hiding it
    return React.cloneElement(children, {
      disabled: true,
      title: disabledTooltip,
      style: {
        ...children.props.style,
        cursor: 'not-allowed',
        opacity: 0.5,
      },
    });
  }

  // Hide element and show fallback (or nothing)
  return <>{fallback}</>;
};

PermissionGate.propTypes = {
  permission: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  mode: PropTypes.oneOf(['any', 'all']),
  children: PropTypes.node.isRequired,
  fallback: PropTypes.node,
  disable: PropTypes.bool,
  hide: PropTypes.bool,
  disabledTooltip: PropTypes.string,
};

export default PermissionGate;
